/**
 * Proofs — `/finance/proofs`.
 *
 * Three reports that publish the evidence they were checked against: the
 * periodic tax return, the fixed-asset register, and the goods-received-not-
 * invoiced reconciliation.
 *
 * They belong together because they share the property that makes them worth
 * reading. Each derives its figures from two or three genuinely independent
 * sources and REFUSES to answer when those disagree. The tax return is
 * computed from posted journals rather than from source documents, and each
 * control account's movement is derived three ways. The register derives cost
 * and depreciation from the subledger and from the control accounts. GRNI
 * derives one side from posted journal lines and the other from receipt lines
 * less what invoices cleared.
 *
 * So when a report is withheld, the screen shows the numbers that disagreed
 * rather than "something went wrong": those numbers are the entire diagnosis,
 * and a plausible-looking figure in their place is how a broken set of books
 * survives to year end.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { listFiscalYears, type FiscalYear } from "@/features/ledger/calendar-api";

import {
  getFixedAssetRegister,
  getGRNIReconciliation,
  getTaxReturn,
  withheldReport,
} from "../reports-api";

export interface ReconciliationsPageProps {
  baseCurrency?: string;
  canReadTaxReturn?: boolean;
  canReadRegister?: boolean;
  canReadGRNI?: boolean;
}

export function ReconciliationsPage({
  baseCurrency = "SAR",
  canReadTaxReturn = false,
  canReadRegister = false,
  canReadGRNI = false,
}: ReconciliationsPageProps) {
  const { t } = useTranslation();
  const [periodId, setPeriodId] = useState("");
  const [currency, setCurrency] = useState(baseCurrency === "" ? "SAR" : baseCurrency);
  const [asOf, setAsOf] = useState("");

  const years = useQuery<FiscalYear[]>({
    queryKey: ["ledger/fiscal-years"],
    queryFn: ({ signal }) => listFiscalYears(signal),
  });
  const periods = useMemo(
    () =>
      (years.data ?? []).flatMap((year) =>
        year.periods.map((period) => ({ id: period.id, label: `${year.code} · ${period.name}` })),
      ),
    [years.data],
  );

  const taxReturn = useQuery({
    queryKey: ["finance/tax-return", periodId],
    queryFn: ({ signal }) => getTaxReturn(periodId, signal),
    enabled: canReadTaxReturn && periodId !== "",
    retry: false,
  });
  const register = useQuery({
    queryKey: ["finance/fixed-asset-register", periodId],
    queryFn: ({ signal }) => getFixedAssetRegister(periodId, signal),
    enabled: canReadRegister && periodId !== "",
    retry: false,
  });
  const grni = useQuery({
    queryKey: ["finance/grni-reconciliation", currency, asOf],
    queryFn: ({ signal }) => getGRNIReconciliation(currency, asOf, signal),
    enabled: canReadGRNI && currency.length === 3,
    retry: false,
  });

  const first = canReadTaxReturn ? "tax" : canReadRegister ? "assets" : "grni";

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("finance.proofs.title", "Proofs")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "finance.proofs.subtitle",
            "Reports that derive their figures twice and refuse to answer when the two disagree. What you see here has already been checked.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="proof-period">{t("finance.proofs.period", "Period")}</Label>
          <Select value={periodId} onValueChange={setPeriodId}>
            <SelectTrigger id="proof-period" className="w-64">
              <SelectValue placeholder={t("finance.proofs.pickPeriod", "Pick a period…")} />
            </SelectTrigger>
            <SelectContent>
              {periods.map((period) => (
                <SelectItem key={period.id} value={period.id}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue={first}>
        <TabsList>
          {canReadTaxReturn ? (
            <TabsTrigger value="tax">{t("finance.proofs.taxTab", "Tax return")}</TabsTrigger>
          ) : null}
          {canReadRegister ? (
            <TabsTrigger value="assets">
              {t("finance.proofs.assetsTab", "Asset register")}
            </TabsTrigger>
          ) : null}
          {canReadGRNI ? (
            <TabsTrigger value="grni">
              {t("finance.proofs.grniTab", "Received, not invoiced")}
            </TabsTrigger>
          ) : null}
        </TabsList>

        {canReadTaxReturn ? (
          <TabsContent value="tax">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("finance.proofs.taxTitle", "Tax return")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t(
                    "finance.proofs.taxHint",
                    "Computed from posted journals, never from source documents, and company-wide: a branch is a site of one legal entity with one registration.",
                  )}
                </p>
                {periodId === "" ? (
                  <p className="text-sm text-muted-foreground">
                    {t("finance.proofs.pickFirst", "Pick a period.")}
                  </p>
                ) : taxReturn.isLoading ? (
                  <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
                ) : taxReturn.data === undefined ? (
                  <WithheldPanel error={taxReturn.error} />
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[36rem] text-sm">
                        <thead>
                          <tr className="border-b text-xs uppercase text-muted-foreground">
                            <th scope="col" className="p-2 text-start">
                              {t("finance.proofs.taxCode", "Code")}
                            </th>
                            <th scope="col" className="p-2 text-end">
                              {t("finance.proofs.outputTax", "Charged on sales")}
                            </th>
                            <th scope="col" className="p-2 text-end">
                              {t("finance.proofs.inputTax", "Recoverable")}
                            </th>
                            <th scope="col" className="p-2 text-end">
                              {t("finance.proofs.netTax", "Net")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxReturn.data.lines.map((line) => (
                            <tr key={line.tax_code_id} className="border-b last:border-0">
                              <td className="p-2">
                                <span dir="ltr" className="font-mono">
                                  {line.code}
                                </span>{" "}
                                <span className="text-xs text-muted-foreground">
                                  {line.jurisdiction}
                                </span>
                              </td>
                              <td className="p-2 text-end">
                                <MoneyDisplay value={line.output_tax} display="none" />
                              </td>
                              <td className="p-2 text-end">
                                <MoneyDisplay value={line.input_tax} display="none" />
                              </td>
                              <td className="p-2 text-end">
                                <MoneyDisplay value={line.net_tax} display="none" />
                              </td>
                            </tr>
                          ))}
                          <tr className="font-medium">
                            <td className="p-2">{t("finance.proofs.total", "Total")}</td>
                            <td className="p-2 text-end">
                              <MoneyDisplay
                                value={taxReturn.data.totals.output_tax}
                                display="none"
                              />
                            </td>
                            <td className="p-2 text-end">
                              <MoneyDisplay
                                value={taxReturn.data.totals.input_tax}
                                display="none"
                              />
                            </td>
                            <td className="p-2 text-end">
                              <MoneyDisplay value={taxReturn.data.totals.net_tax} display="none" />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "finance.proofs.tieOut",
                        "Each control account's movement was derived three ways and agreed. {{untagged}} posted line(s) hit a tax account with no code on them.",
                        {
                          untagged: taxReturn.data.tie_out.reduce(
                            (sum, entry) => sum + entry.untagged_line_count,
                            0,
                          ),
                        },
                      )}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}

        {canReadRegister ? (
          <TabsContent value="assets">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("finance.proofs.assetsTitle", "Fixed-asset register")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t(
                    "finance.proofs.assetsHint",
                    "Net book value as of the period end. An asset is judged present by its disposal date, so a register run for March still shows an asset sold in June.",
                  )}
                </p>
                {periodId === "" ? (
                  <p className="text-sm text-muted-foreground">
                    {t("finance.proofs.pickFirst", "Pick a period.")}
                  </p>
                ) : register.isLoading ? (
                  <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
                ) : register.data === undefined ? (
                  <WithheldPanel error={register.error} />
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[40rem] text-sm">
                        <thead>
                          <tr className="border-b text-xs uppercase text-muted-foreground">
                            <th scope="col" className="p-2 text-start">
                              {t("finance.proofs.class", "Class")}
                            </th>
                            <th scope="col" className="p-2 text-end">
                              {t("finance.proofs.assetCount", "Assets")}
                            </th>
                            <th scope="col" className="p-2 text-end">
                              {t("finance.proofs.cost", "Cost")}
                            </th>
                            <th scope="col" className="p-2 text-end">
                              {t("finance.proofs.depreciation", "Depreciated")}
                            </th>
                            <th scope="col" className="p-2 text-end">
                              {t("finance.proofs.netBookValue", "Net book value")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {register.data.class_totals.map((total) => (
                            <tr key={total.class_id} className="border-b last:border-0">
                              <td className="p-2">
                                <span className="block">{total.class_name}</span>
                                <span dir="ltr" className="font-mono text-xs text-muted-foreground">
                                  {total.class_code}
                                </span>
                              </td>
                              <td className="p-2 text-end tabular-nums" dir="ltr">
                                {total.asset_count}
                              </td>
                              <td className="p-2 text-end">
                                <MoneyDisplay value={total.acquisition_cost} display="none" />
                              </td>
                              <td className="p-2 text-end">
                                <MoneyDisplay
                                  value={total.accumulated_depreciation}
                                  display="none"
                                />
                              </td>
                              <td className="p-2 text-end">
                                <MoneyDisplay value={total.net_book_value} display="none" />
                              </td>
                            </tr>
                          ))}
                          <tr className="font-medium">
                            <td className="p-2">{t("finance.proofs.total", "Total")}</td>
                            <td className="p-2 text-end tabular-nums" dir="ltr">
                              {register.data.rows.length}
                            </td>
                            <td className="p-2 text-end">
                              <MoneyDisplay
                                value={register.data.total_acquisition_cost}
                                display="none"
                              />
                            </td>
                            <td className="p-2 text-end">
                              <MoneyDisplay
                                value={register.data.total_accumulated_depreciation}
                                display="none"
                              />
                            </td>
                            <td className="p-2 text-end">
                              <MoneyDisplay
                                value={register.data.total_net_book_value}
                                display="none"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <Badge
                        variant={register.data.reconciliation.balanced ? "success" : "destructive"}
                      >
                        {t("finance.proofs.tiesOut", "Ties to the control accounts")}
                      </Badge>
                      <span className="text-muted-foreground">
                        {t(
                          "finance.proofs.registerVsLedger",
                          "Subledger and ledger were derived separately and agree.",
                        )}
                      </span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}

        {canReadGRNI ? (
          <TabsContent value="grni">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("finance.proofs.grniTitle", "Received, not invoiced")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t(
                    "finance.proofs.grniHint",
                    "The clearing account against the accrual still carried by receipt lines. The two sides come from different places on purpose — a tie-out that computed one from the other would prove nothing.",
                  )}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="grni-currency">
                      {t("finance.proofs.currency", "Currency")}
                    </Label>
                    <Input
                      id="grni-currency"
                      dir="ltr"
                      maxLength={3}
                      className="w-24 font-mono uppercase"
                      value={currency}
                      onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="grni-as-of">{t("finance.proofs.asOf", "As of")}</Label>
                    <Input
                      id="grni-as-of"
                      type="date"
                      dir="ltr"
                      value={asOf}
                      onChange={(event) => setAsOf(event.target.value)}
                    />
                  </div>
                </div>
                {grni.isLoading ? (
                  <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
                ) : grni.data === undefined ? (
                  <WithheldPanel error={grni.error} />
                ) : (
                  <>
                    <dl className="grid gap-2 sm:grid-cols-2">
                      <Figure
                        label={t("finance.proofs.subledger", "From the receipts")}
                        value={grni.data.subledger_balance}
                      />
                      <Figure
                        label={t("finance.proofs.ledger", "From the ledger")}
                        value={grni.data.ledger_balance}
                      />
                      <Figure
                        label={t("finance.proofs.accrued", "Accrued by receipts")}
                        value={grni.data.accrued_total}
                      />
                      <Figure
                        label={t("finance.proofs.cleared", "Cleared by invoices")}
                        value={grni.data.cleared_total}
                      />
                    </dl>
                    <p className="text-sm">
                      <Badge variant={grni.data.balanced ? "success" : "destructive"}>
                        {grni.data.balanced
                          ? t("finance.proofs.agrees", "The two sides agree")
                          : t("finance.proofs.disagrees", "The two sides disagree")}
                      </Badge>
                    </p>
                    {grni.data.lines.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t("finance.proofs.nothingOpen", "Nothing is received and uninvoiced.")}
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[36rem] text-sm">
                          <thead>
                            <tr className="border-b text-xs uppercase text-muted-foreground">
                              <th scope="col" className="p-2 text-start">
                                {t("finance.proofs.receiptLine", "Receipt line")}
                              </th>
                              <th scope="col" className="p-2 text-end">
                                {t("finance.proofs.received", "Received")}
                              </th>
                              <th scope="col" className="p-2 text-end">
                                {t("finance.proofs.invoiced", "Invoiced")}
                              </th>
                              <th scope="col" className="p-2 text-end">
                                {t("finance.proofs.open", "Still open")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {grni.data.lines.map((line) => (
                              <tr key={line.grn_line_id} className="border-b last:border-0">
                                <td className="p-2 font-mono text-xs" dir="ltr">
                                  {line.grn_line_id.slice(0, 8)}
                                </td>
                                <td className="p-2 text-end tabular-nums" dir="ltr">
                                  {line.qty_accepted}
                                </td>
                                <td className="p-2 text-end tabular-nums" dir="ltr">
                                  {line.qty_invoiced}
                                </td>
                                <td className="p-2 text-end">
                                  <MoneyDisplay value={line.open_value} display="none" />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: { amount: string; currency: string } }) {
  return (
    <div className="rounded-md border p-3">
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className="text-lg">
        <MoneyDisplay value={value} />
      </dd>
    </div>
  );
}

/** A withheld report is a diagnosis, not a failure message: the numbers that
 *  disagreed are the whole of it, so they are shown. */
function WithheldPanel({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const withheld = withheldReport(error);
  if (withheld === null) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t("finance.proofs.error", "The report could not be read.")}
      </p>
    );
  }
  return (
    <div className="space-y-2" role="alert">
      <p className="text-sm text-destructive">
        {t(
          "finance.proofs.withheld",
          "This report was withheld because its two derivations disagree. Publishing a plausible figure here is how a broken set of books survives to year end.",
        )}
      </p>
      <ul className="space-y-1 text-sm">
        {Object.entries(withheld)
          .filter(([key]) => key !== "type" && key !== "title" && key !== "status")
          .map(([key, value]) => (
            <li key={key} className="flex flex-wrap items-center justify-between gap-3">
              <span className="font-mono text-xs" dir="ltr">
                {key}
              </span>
              <span dir="ltr" className="tabular-nums">
                {typeof value === "string" || typeof value === "number"
                  ? String(value)
                  : JSON.stringify(value)}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
}
